/**
 * DevOpsTab — iter86.9 · Platform Console → Governance → Developer Agency (UI label)
 *
 * The Agent Operating Agreement: per-domain autonomy matrix (tunable,
 * audited) that agents/human developers read FIRST before acting.
 * Canon / starter-kit artifact (feature: platform.dev_ops_agreement).
 * Source of truth file: memory/AGENT_OPERATING_AGREEMENT.md (regenerated
 * on every change; audit in agent_agreement_audit.md).
 */
import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Bot, Lock, History, Loader2 } from "lucide-react";

const LEVEL_PILL = {
    execute: "border-emerald-500/40 text-emerald-300",
    execute_and_report: "border-cyber/40 text-cyber",
    propose_first: "border-amber-500/40 text-amber-300",
    never: "border-rose-500/40 text-rose-300",
};

function DomainRow({ row, levels, onSave }) {
    const [level, setLevel] = useState(row.level);
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);
    const dirty = level !== row.level;

    const save = async () => {
        setBusy(true);
        await onSave(row.domain_id, level, reason, () => setReason(""));
        setBusy(false);
    };

    return (
        <div className="border border-zinc-800 rounded p-2.5 bg-black/30 space-y-1.5" data-testid={`agreement-domain-${row.domain_id}`}>
            <div className="flex items-center gap-2 flex-wrap">
                <code className="text-[11px] font-bold text-white">{row.domain_id}</code>
                <span className="text-[11px] text-zinc-400">{row.label}</span>
                {row.locked && (
                    <span className="pill text-[9px] uppercase border-zinc-600 text-zinc-400 inline-flex items-center gap-1" data-testid={`agreement-locked-${row.domain_id}`}>
                        <Lock className="w-2.5 h-2.5" /> locked
                    </span>
                )}
                <span className={`ml-auto pill text-[9px] font-bold uppercase border ${LEVEL_PILL[row.level]}`} data-testid={`agreement-level-${row.domain_id}`}>
                    {row.level}
                </span>
            </div>
            <p className="text-[10px] text-zinc-500">{row.notes}</p>
            {!row.locked && (
                <div className="flex items-center gap-2 flex-wrap">
                    <select
                        value={level}
                        onChange={(e) => setLevel(e.target.value)}
                        data-testid={`agreement-select-${row.domain_id}`}
                        className="brutal-input text-[10px] py-1 w-auto"
                    >
                        {levels.map((l) => <option key={l.id} value={l.id}>{l.id}</option>)}
                    </select>
                    {dirty && (
                        <>
                            <input
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                placeholder="Reason (required — audited)"
                                data-testid={`agreement-reason-${row.domain_id}`}
                                className="brutal-input text-[10px] py-1 flex-1 min-w-[200px]"
                            />
                            <button
                                type="button"
                                onClick={save}
                                disabled={busy || !reason.trim()}
                                data-testid={`agreement-save-${row.domain_id}`}
                                className="brutal-btn text-[10px] py-1 px-2 disabled:opacity-50"
                            >
                                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save"}
                            </button>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

export default function DevOpsTab({ viewerRole }) {
    const [data, setData] = useState(null);
    const isAdmin = viewerRole === "platform_admin";

    const load = useCallback(() => {
        api.devOpsAgreement().then(setData).catch((e) =>
            toast.error(e?.response?.data?.detail || "Failed to load agreement"));
    }, []);
    useEffect(() => { load(); }, [load]);

    const onSave = async (domainId, level, reason, clear) => {
        try {
            await api.devOpsAgreementSet(domainId, { level, reason });
            toast.success(`${domainId} → ${level} (audited + AGENT_OPERATING_AGREEMENT.md regenerated)`);
            clear();
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Change rejected");
        }
    };

    if (!data) return <div className="text-xs text-zinc-500 italic p-4">Loading agreement…</div>;

    return (
        <div className="space-y-6" data-testid="dev-ops-tab">
            <div className="brutal-card p-4 space-y-2">
                <div className="flex items-center gap-3">
                    <Bot className="w-5 h-5 text-cyber" />
                    <div>
                        <h2 className="font-display font-extrabold text-base uppercase tracking-wide text-zinc-200">
                            Agent Operating Agreement
                        </h2>
                        <p className="text-[10px] text-zinc-500">
                            The first-read guide for agents and human developers: when to act vs. check in.
                            Source of truth: <code>memory/AGENT_OPERATING_AGREEMENT.md</code> (regenerated on
                            every change · Docs → AGENT_OPERATING_AGREEMENT). Canon / starter-kit artifact.
                        </p>
                    </div>
                </div>
                <ol className="list-decimal ml-5 space-y-0.5">
                    {data.principles.map((p, i) => (
                        <li key={i} className="text-[11px] text-zinc-400">{p}</li>
                    ))}
                </ol>
                <div className="flex flex-wrap gap-1 pt-1">
                    {data.levels.map((l) => (
                        <span key={l.id} className={`pill text-[9px] font-bold uppercase border ${LEVEL_PILL[l.id]}`} title={l.meaning}>
                            {l.id}
                        </span>
                    ))}
                    <span className="text-[9px] text-zinc-600 self-center">— hover for meanings</span>
                </div>
            </div>

            <div className="brutal-card p-4 space-y-2" data-testid="agreement-matrix">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
                    Autonomy matrix {isAdmin ? "· tune levels below (reason required, audited)" : "· read-only for developers"}
                </p>
                <div className="space-y-1.5">
                    {data.domains.map((d) => (
                        <DomainRow key={d.domain_id} row={d} levels={data.levels} onSave={isAdmin ? onSave : async () => toast.error("Platform admin only")} />
                    ))}
                </div>
            </div>

            <div className="brutal-card p-4 space-y-2" data-testid="agreement-escalation">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
                    Escalation triggers (override the matrix upward)
                </p>
                <ul className="list-disc ml-5">
                    {data.escalation_triggers.map((t, i) => (
                        <li key={i} className="text-[11px] text-zinc-400">{t}</li>
                    ))}
                </ul>
            </div>

            <div className="brutal-card p-4 space-y-2" data-testid="agreement-audit">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 inline-flex items-center gap-1.5">
                    <History className="w-3.5 h-3.5" /> Tuning audit trail (who changed what)
                </p>
                {data.audit.length === 0 && (
                    <p className="text-[11px] text-zinc-600 italic">No tuning changes yet — matrix is at seeded defaults.</p>
                )}
                {data.audit.map((a) => (
                    <p key={a.id} className="text-[10px] text-zinc-400" data-testid={`agreement-audit-${a.id}`}>
                        <span className="text-zinc-500">{a.at.slice(0, 16).replace("T", " ")}</span>
                        {" — "}<code className="text-white">{a.domain_id}</code>
                        {" "}{a.from_level} → <span className="font-bold text-cyber">{a.to_level}</span>
                        {" by "}{a.by} — <span className="italic">{a.reason}</span>
                    </p>
                ))}
            </div>
        </div>
    );
}
